import moment from "moment";
import {
    APPLICANT_TYPES,
    GENDER_TYPES,
    MARITIAL_STATUS_TYPES,
    PROFESSION_TYPES,
    NATURE_OF_ASSOCIATION_TYPES,
    RESIDENCE_TYPES,
    BANK_ACCOUNT_TYPES,
    ADDRESSES_TYPES,
    CONSTITUTION_TYPES
} from "constants/enums";
import { CHECKLIST_STATE, DOC_STATUS } from "./constants";

/* ------------------------------------------------------------------ values */

/** Immutable-or-plain read. Every LOS store slice mixes the two. */
export const get = (obj, key, fallback = null) => {
    if (obj === null || obj === undefined) return fallback;
    const value = obj.get ? obj.get(key) : obj[key];
    return value === undefined ? fallback : value;
};

export const getIn = (obj, path, fallback = null) =>
    path.reduce((acc, key) => get(acc, key, null), obj) || fallback;

export const toArray = value => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    // Immutable Map.toArray() yields [key, value] entries, so go through
    // toList() first — that gives the values for both Map and List.
    if (value.toList) return value.toList().toArray();
    if (value.toArray) return value.toArray();
    if (typeof value === "object") return Object.values(value);
    return [];
};

const INR = new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 0
});

export const money = value => {
    const num = Number(value);
    if (value === null || value === undefined || value === "" || isNaN(num)) {
        return "—";
    }
    return `₹${INR.format(num)}`;
};

export const percent = value => {
    const num = Number(value);
    if (value === null || value === undefined || value === "" || isNaN(num)) {
        return "—";
    }
    return `${num % 1 === 0 ? num : num.toFixed(2)}%`;
};

export const date = (value, format = "DD MMM YYYY") => {
    if (!value) return "—";
    const m = moment.isMoment(value) ? value : moment(value);
    return m.isValid() ? m.format(format) : "—";
};

export const isoDate = value => {
    if (!value) return "";
    const m = moment.isMoment(value) ? value : moment(value);
    return m.isValid() ? m.format("YYYY-MM-DD") : "";
};

/** Enum id -> label. Enums in constants/enums are label-keyed Immutable Maps. */
export const enumLabel = (enumMap, id, fallback = "—") => {
    if (id === null || id === undefined || id === "") return fallback;
    return enumMap.findKey(value => Number(value) === Number(id)) || fallback;
};

export const enumOptions = enumMap =>
    enumMap
        .map((value, label) => ({ value, label }))
        .toList()
        .toArray();

const squash = value =>
    String(value || "")
        .replace(/[^a-z0-9]/gi, "")
        .toLowerCase();

/** Two names match when they agree ignoring case, spacing and punctuation. */
export const namesMatch = (a, b) => {
    const left = squash(a);
    const right = squash(b);
    if (!left || !right) return false;
    return left === right;
};

export const datesMatch = (a, b) => {
    if (!a || !b) return false;
    return isoDate(a) === isoDate(b);
};

/* -------------------------------------------------------------- applicants */

export const applicantName = associate => {
    const parts = [
        get(associate, "first_name"),
        get(associate, "middle_name"),
        get(associate, "last_name")
    ].filter(Boolean);
    return parts.join(" ").trim() || "Unnamed applicant";
};

export const initialsOf = name =>
    String(name || "")
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map(part => part.charAt(0).toUpperCase())
        .join("") || "?";

// Master fields come back as `{ id, name }`, a bare name, or a bare id (an
// un-nested FK, e.g. `city.district: 569`). The edit form needs the object (it
// posts the id), so normalise to the object shape — and never print an id as
// the name.
const masterObject = value => {
    if (value === null || value === undefined || value === "") return null;
    if (typeof value === "object") {
        return { id: get(value, "id") || null, name: get(value, "name") || "" };
    }
    if (/^\d+$/.test(String(value).trim())) return { id: Number(value), name: "" };
    return { id: null, name: String(value) };
};

// First candidate that carries a name, else the first one we have at all.
const firstMaster = (...values) => {
    const masters = values.map(masterObject).filter(Boolean);
    return masters.find(master => master.name) || masters[0] || null;
};

/**
 * District to seed an address form with. Picking an existing city leaves the
 * row's own `district` null and carries it on the city master, so read both —
 * otherwise the form opens, or copies "Same as residence", with a blank
 * district for an address that has one.
 */
const namedMaster = value => (value && typeof value === "object" && value.name ? value : null);
// The address API returns the city's district as a bare id when the row has
// none of its own. Carry the id even without a name, so a copy still saves
// the right district (district_id) instead of dropping it.
const idOnlyMaster = value => {
    if (value === null || value === undefined || value === "") return null;
    if (typeof value === "object") return get(value, "id") ? { id: get(value, "id"), name: get(value, "name") || "" } : null;
    return /^\d+$/.test(String(value)) ? { id: Number(value), name: "" } : null;
};

export const resolvedDistrict = raw =>
    namedMaster(get(raw, "district")) ||
    namedMaster(getIn(raw, ["city", "district"])) ||
    idOnlyMaster(get(raw, "district")) ||
    idOnlyMaster(getIn(raw, ["city", "district"])) ||
    null;

export const buildAddress = address => {
    if (!address || !get(address, "id")) return null;
    return {
        id: get(address, "id"),
        // The edit modal seeds AddAddressForm from the untouched API row.
        raw: address && address.toJS ? address.toJS() : address,
        address_line_1: get(address, "address_line_1"),
        address_line_2: get(address, "address_line_2"),
        pincode: get(address, "pincode"),
        city: masterObject(get(address, "city")),
        // The district is only entered by hand when the operator mints a new
        // city; picking an existing one leaves the row's own `district` null
        // and carries it on the city master instead. Read both, or the panel
        // reports "Not captured" for an address that has a district.
        // Either side may be a bare FK id, so take whichever one has a name.
        district: firstMaster(get(address, "district"), getIn(address, ["city", "district"])),
        state: masterObject(get(address, "state")),
        country: get(address, "country"),
        ownership_type: get(address, "ownership_type"),
        ownership: enumLabel(RESIDENCE_TYPES, get(address, "ownership_type")),
        residing_since: get(address, "residing_since"),
        address_type: get(address, "address_type"),
        is_pv_done: get(address, "is_pv_done"),
        is_communication_address: get(address, "is_communication_address")
    };
};

const sameAddress = (a, b) => {
    if (!a || !b) return false;
    return (
        squash(a.address_line_1) === squash(b.address_line_1) &&
        String(a.pincode || "") === String(b.pincode || "")
    );
};

/**
 * Turn the `company/case/<id>/associate-and-address-detail/` payload into the
 * view model the Case reference panel renders. One entry per applicant, with
 * the residence / permanent address already resolved and the KYC cross-checks
 * (PAN name vs name, Aadhaar DOB vs DOB, …) already evaluated.
 */
export const buildApplicants = associates => {
    return toArray(associates)
        .filter(associate => get(associate, "is_active") !== false)
        .map(associate => {
            const name = applicantName(associate);
            const dob = get(associate, "dob");
            const permanent = buildAddress(get(associate, "permanent_address"));
            const residence = buildAddress(get(associate, "residence_address"));
            const checks = {
                name_pan: namesMatch(get(associate, "name_pan"), name),
                dob_pan: datesMatch(get(associate, "dob_pan"), dob),
                name_aadhar: namesMatch(get(associate, "name_aadhar"), name),
                dob_aadhar: datesMatch(get(associate, "dob_aadhar"), dob),
                name_gst: namesMatch(get(associate, "name_gst"), name)
            };
            return {
                id: get(associate, "id"),
                raw: associate,
                name,
                initials: initialsOf(name),
                type: get(associate, "type"),
                typeLabel: enumLabel(APPLICANT_TYPES, get(associate, "type"), "—"),
                cibil:
                    get(associate, "personal_cibil_score") ||
                    get(associate, "cibil_score"),
                isMainBorrower: !!get(associate, "is_main_borrower"),
                isKeyContact: !!get(associate, "is_key_contact"),
                keySource: get(associate, "key_source") || {},
                permanent,
                residence,
                residenceMirrorsPermanent: sameAddress(residence, permanent),
                checks,
                fields: {
                    first_name: get(associate, "first_name"),
                    last_name: get(associate, "last_name"),
                    gender: get(associate, "gender"),
                    genderLabel: enumLabel(GENDER_TYPES, get(associate, "gender")),
                    dob,
                    marital_status: get(associate, "marital_status"),
                    maritalLabel: enumLabel(
                        MARITIAL_STATUS_TYPES,
                        get(associate, "marital_status")
                    ),
                    nature_of_association: get(associate, "nature_of_association"),
                    titleLabel: enumLabel(
                        NATURE_OF_ASSOCIATION_TYPES,
                        get(associate, "nature_of_association")
                    ),
                    profession: get(associate, "profession"),
                    professionLabel: enumLabel(
                        PROFESSION_TYPES,
                        get(associate, "profession")
                    ),
                    father_name: get(associate, "father_name"),
                    spouse_name: get(associate, "spouse_name"),
                    mother_name: get(associate, "mother_name"),
                    phone: get(associate, "phone"),
                    phone_residence: get(associate, "phone_residence"),
                    phone_office: get(associate, "phone_office"),
                    email: get(associate, "email"),
                    pan: get(associate, "pan"),
                    aadhar: get(associate, "aadhar"),
                    name_pan: get(associate, "name_pan"),
                    dob_pan: get(associate, "dob_pan"),
                    name_aadhar: get(associate, "name_aadhar"),
                    dob_aadhar: get(associate, "dob_aadhar"),
                    name_gst: get(associate, "name_gst"),
                    name_pdeed: get(associate, "name_pdeed"),
                    annual_income: get(associate, "annual_income"),
                    shareholding: get(associate, "shareholding")
                }
            };
        });
};

/* -------------------------------------------------------------- scheme */

/**
 * The scheme a case is mapped on is served as one description string per
 * supply chain, the same one the Account/Facility Details table prints:
 *
 *   Scheme ID - 15780 , Default Scheme (SCF) 12066|(TL) 22590|Margin: 10.00% |
 *   Security Deposit: 0.00% | - | Tenor: 75 | Overdue Tenor: 75 | ROI: 18.25%
 *   … | Cooling Period: 30 | Cashback Sharing: 100.00% (Dealer)
 *   is_adhoc_applicable_on_overdue : False additional_roi: 12.000
 *
 * It is the only place the real numbers live — the case record's own `scheme`
 * carries almost none of them, which is why reading ROI, tenor and margin off
 * that record left those cards empty while the description beside them was
 * full. Parsed into rows here, so the panel can lay them out.
 *
 * Segments are `|`-separated; within one, a value never contains a colon, so a
 * run of pairs jammed together splits on the last word before each colon.
 */
const humanLabel = label => {
    if (!label) return label;
    // The tail of the description carries raw field names
    // (`additional_roi`) among the printed ones ("Cooling Period").
    if (label.indexOf("_") === -1) return label;
    const words = label.replace(/_/g, " ").trim();
    return words.charAt(0).toUpperCase() + words.slice(1);
};

export const parseSchemeDescription = text => {
    const raw = typeof text === "string" ? text : "";
    if (!raw.trim()) return [];
    const rows = [];

    raw.split("|").forEach(segment => {
        const part = segment.trim();
        if (!part || part === "-") return;

        if (part.indexOf(":") === -1) {
            // "Scheme ID - 15780 , Default Scheme (SCF) 12066", or a bare
            // continuation like "(TL) 22590".
            const dash = part.match(/^([A-Za-z][A-Za-z ]*?)\s+-\s+(.+)$/);
            rows.push(
                dash
                    ? { label: dash[1].trim(), value: dash[2].trim() }
                    : { label: null, value: part }
            );
            return;
        }

        const chunks = part.split(":");
        let label = chunks[0].trim();
        for (let i = 1; i < chunks.length; i += 1) {
            const last = i === chunks.length - 1;
            let value = chunks[i];
            let next = null;
            if (!last) {
                // This chunk is "<value of the current label> <next label>".
                const split = value.match(/^([\s\S]*?)\s+(\S+)\s*$/);
                if (split) {
                    value = split[1];
                    next = split[2];
                } else {
                    next = value.trim();
                    value = "";
                }
            }
            const trimmed = String(value).trim();
            if (label || trimmed) {
                rows.push({ label: humanLabel(label) || null, value: trimmed });
            }
            label = next;
        }
    });

    return rows;
};

/* ---------------------------------------------------- company addresses/bank */

export const buildCompanyAddresses = addresses =>
    toArray(addresses)
        // Applicant residence / permanent addresses hang off the same list and
        // are rendered inside each applicant card instead.
        .filter(address => get(address, "is_active") !== false && !get(address, "associate"))
        .map(address => {
            const built = buildAddress(address) || {};
            const rows = [
                { label: "Address Line 1", value: built.address_line_1, span: true },
                { label: "Address Line 2", value: built.address_line_2 },
                { label: "Pincode", value: built.pincode },
                { label: "City", value: built.city && built.city.name },
                { label: "District", value: built.district && built.district.name },
                { label: "State", value: built.state && built.state.name },
                { label: "Ownership", value: built.ownership },
                // One date format across the workspace — see `date`'s default.
                { label: "Residing Since", value: built.residing_since ? date(built.residing_since) : null }
            ].map(row => ({
                ...row,
                missing: row.value === null || row.value === undefined || row.value === "" || row.value === "—"
            }));
            return {
                id: built.id,
                raw: built.raw,
                addressType: built.address_type,
                title: enumLabel(ADDRESSES_TYPES, built.address_type, "Address"),
                ownership: built.ownership,
                rows,
                missingCount: rows.filter(row => row.missing).length
            };
        });

export const buildBankAccounts = accounts =>
    toArray(accounts)
        .filter(account => get(account, "is_active") !== false)
        .map(account => {
            const branch = get(account, "bank_branch") || {};
            return {
                id: get(account, "id"),
                // The bank form and the key-account switch both write through
                // BankAccountRecord, so keep the record itself alongside.
                record: account,
                bank: get(branch, "bank_name") || "—",
                branch: get(branch, "name"),
                ifsc: get(branch, "ifsc_code"),
                holder: get(account, "account_holder_name"),
                number: get(account, "account_number"),
                limit: get(account, "account_limit"),
                typeLabel: enumLabel(
                    BANK_ACCOUNT_TYPES,
                    get(account, "account_type"),
                    "—"
                ),
                isKey: !!get(account, "is_key_account")
            };
        });

/* --------------------------------------------------------------- documents */

const docThumbKind = name => {
    const key = String(name || "").toLowerCase();
    if (key.indexOf("pan") >= 0 || key.indexOf("aadha") >= 0) return "idCard";
    if (key.indexOf("photo") >= 0 || key.indexOf("image") >= 0) return "image";
    return "file";
};

export const docStatusMeta = status => {
    if (Number(status) === DOC_STATUS.VERIFIED) {
        return { key: "verified", label: "Verified", text: "Verified & tagged", tone: "ok", dot: "#12b76a" };
    }
    if (Number(status) === DOC_STATUS.REJECTED) {
        return { key: "rejected", label: "Rejected", text: "Rejected on review", tone: "bad", dot: "#d92d20" };
    }
    if (Number(status) === DOC_STATUS.REQUESTED) {
        return { key: "requested", label: "Requested", text: "Requested from customer", tone: "neutral", dot: "#d0d5dd" };
    }
    if (Number(status) === DOC_STATUS.ARCHIVED) {
        return { key: "archived", label: "Archived", text: "Archived off the case", tone: "neutral", dot: "#98a2b3" };
    }
    return { key: "pending", label: "Pending", text: "Awaiting review", tone: "warn", dot: "#f79009" };
};

/**
 * Normalise the company-documents slice into rows the workspace renders.
 * `files` is the same Immutable list the Documents Review tab consumes, so the
 * two tabs always agree on what is on file.
 */
export const buildDocuments = (files, applicants, companyName) => {
    const byId = {};
    applicants.forEach(applicant => {
        byId[applicant.id] = applicant.name;
    });
    return toArray(files)
        .filter(file => get(file, "is_active") !== false)
        .map(file => {
            const document = get(file, "document") || {};
            const associateId = get(file, "associate");
            const associateName = associateId ? byId[associateId] : null;
            const status = get(file, "document_status");
            const parsed = get(file, "parse_data") || {};
            return {
                id: get(file, "id"),
                raw: file,
                name: get(file, "file_name") || get(document, "document_name") || "Untitled file",
                fileUrl: get(file, "file"),
                mime: get(file, "file_mime_type"),
                documentId: get(document, "id"),
                documentName: get(document, "document_name") || "Untagged",
                documentOption: get(file, "document_option"),
                category: getIn(document, ["document_category", "name"]),
                associateId: associateId || null,
                owner: associateName
                    ? `Applicant · ${associateName}`
                    : `Company · ${companyName || "—"}`,
                isApplicantLevel: !!associateId,
                status,
                statusMeta: docStatusMeta(status),
                remarks: get(file, "remarks"),
                requestRemarks: get(file, "request_remarks"),
                thumbKind: docThumbKind(get(document, "document_name") || get(file, "file_name")),
                parsedName:
                    get(parsed, "name") ||
                    get(parsed, "full_name") ||
                    get(parsed, "registered_name") ||
                    null,
                parsedId:
                    get(parsed, "pan") ||
                    get(parsed, "aadhaar") ||
                    get(parsed, "aadhar") ||
                    get(parsed, "gstin") ||
                    get(parsed, "number") ||
                    null,
                modified: get(file, "modified_date")
            };
        });
};

/* --------------------------------------------------------------- checklist */

/**
 * Per-field PS-Kit rules. `source` is the system of record the ops team
 * validates the value against (from the PS Kit validation sheet); `read`
 * pulls the captured value and `verify` (optional) decides whether a captured
 * value is actually *correct* — that is what separates "Wrong" from "Missing".
 */
const AADHAAR = "DigiLocker / uploaded Aadhaar";
const PAN_PORTAL = "PAN details from Income Tax portal";
const SALES_APP = "Sales input from OneApp";
const SYSTEM = "System default";
const GST_MASTER = "GST details from Masters India";
const MANUAL = "Entered in LOS";

export const FORM_CHECKLIST_RULES = [
    { key: "first_name", label: "First & Middle Name", source: AADHAAR, mandatory: true },
    { key: "last_name", label: "Last Name", source: AADHAAR, mandatory: true },
    { key: "aadhar", label: "Aadhaar Number", source: AADHAAR, mandatory: true },
    { key: "gender", label: "Gender", source: PAN_PORTAL, mandatory: true },
    { key: "marital_status", label: "Marital Status", source: SALES_APP, mandatory: true },
    { key: "father_name", label: "Father Name", source: PAN_PORTAL, mandatory: true },
    { key: "spouse_name", label: "Spouse Name", source: MANUAL, mandatory: false },
    { key: "mother_name", label: "Mother Maiden Name", source: MANUAL, mandatory: false },
    { key: "dob", label: "Date of Birth", source: PAN_PORTAL, mandatory: true },
    { key: "phone", label: "Phone Number", source: SALES_APP, mandatory: true },
    { key: "phone_residence", label: "Alternate Phone Number", source: SALES_APP, mandatory: false },
    { key: "phone_office", label: "Office Phone / Landline", source: MANUAL, mandatory: false },
    { key: "email", label: "Email", source: SALES_APP, mandatory: true },
    { key: "nature_of_association", label: "Title", source: SYSTEM, mandatory: true },
    { key: "profession", label: "Profession", source: MANUAL, mandatory: false },
    { key: "pan", label: "PAN No.", source: PAN_PORTAL, mandatory: true },
    { key: "annual_income", label: "Annual Income", source: MANUAL, mandatory: false },
    { key: "shareholding", label: "Shareholding", source: SYSTEM, mandatory: true },
    { key: "name_pan", label: "Name as on PAN", source: PAN_PORTAL, mandatory: true, check: "name_pan" },
    { key: "dob_pan", label: "Date of Birth as on PAN", source: PAN_PORTAL, mandatory: true, check: "dob_pan" },
    { key: "name_aadhar", label: "Name as on Aadhaar", source: AADHAAR, mandatory: true, check: "name_aadhar" },
    { key: "dob_aadhar", label: "Date of Birth as on Aadhaar", source: AADHAAR, mandatory: true, check: "dob_aadhar" },
    { key: "name_gst", label: "Name as on GST", source: GST_MASTER, mandatory: true, check: "name_gst" }
];

const hasValue = value =>
    value !== null && value !== undefined && String(value).trim() !== "";

// How a checklist row edits in place: enum fields as a select, dates as a
// date picker, everything else as text. Same options the applicant card uses.
const FORM_FIELD_INPUTS = {
    gender: { type: "select", enumMap: GENDER_TYPES },
    marital_status: { type: "select", enumMap: MARITIAL_STATUS_TYPES },
    nature_of_association: { type: "select", enumMap: NATURE_OF_ASSOCIATION_TYPES },
    profession: { type: "select", enumMap: PROFESSION_TYPES },
    dob: { type: "date" },
    dob_pan: { type: "date" },
    dob_aadhar: { type: "date" }
};

const inputFor = key => {
    const spec = FORM_FIELD_INPUTS[key];
    if (!spec) return { type: "text" };
    if (spec.type === "select") return { type: "select", options: enumOptions(spec.enumMap) };
    return { type: spec.type };
};

const displayFor = (key, value) => {
    if (!hasValue(value)) return null;
    const spec = FORM_FIELD_INPUTS[key];
    if (spec && spec.type === "select") return enumLabel(spec.enumMap, value, String(value));
    if (spec && spec.type === "date") {
        const m = moment(value);
        return m.isValid() ? m.format("DD MMM YYYY") : String(value);
    }
    if (key === "shareholding") return `${value}%`;
    return String(value);
};

/** One-line address for a checklist row. */
export const addressText = address => {
    if (!address) return null;
    const name = master => (master && master.name) || null;
    return [
        address.address_line_1,
        address.address_line_2,
        name(address.city),
        name(address.district),
        name(address.state),
        address.pincode
    ]
        .filter(hasValue)
        .join(", ");
};

/**
 * Build the form half of the PS Kit checklist for one applicant.
 * Complete  — captured, and (where a cross-check exists) matching its source.
 * Wrong     — captured but disagreeing with the source of truth.
 * Missing   — not captured at all.
 */
export const buildFormChecklist = applicant => {
    const rows = FORM_CHECKLIST_RULES.map(rule => {
        const value = applicant.fields[rule.key];
        let status = CHECKLIST_STATE.COMPLETE;
        let reason = null;
        if (!hasValue(value)) {
            status = CHECKLIST_STATE.MISSING;
            reason = `Not captured — expected from ${rule.source}`;
        } else if (rule.check && applicant.checks[rule.check] === false) {
            status = CHECKLIST_STATE.WRONG;
            reason = `Does not match the applicant name/DOB on record (${rule.source})`;
        }
        return {
            id: `${applicant.id}-${rule.key}`,
            field: rule.key,
            applicantId: applicant.id,
            value: hasValue(value) ? value : null,
            display: displayFor(rule.key, value),
            input: inputFor(rule.key),
            name: rule.label,
            source: rule.source,
            mandatory: rule.mandatory,
            applicant: applicant.name,
            status,
            reason
        };
    });
    // Addresses come off the address records, not the associate row.
    rows.push({
        id: `${applicant.id}-permanent_address`,
        field: "permanent_address",
        applicantId: applicant.id,
        display: addressText(applicant.permanent),
        name: "Permanent Address",
        source: AADHAAR,
        mandatory: true,
        applicant: applicant.name,
        status: applicant.permanent ? CHECKLIST_STATE.COMPLETE : CHECKLIST_STATE.MISSING,
        reason: applicant.permanent ? null : `Not captured — expected from ${AADHAAR}`
    });
    rows.push({
        id: `${applicant.id}-residence_address`,
        field: "residence_address",
        applicantId: applicant.id,
        display: addressText(applicant.residence),
        name: "Residence Address",
        source: AADHAAR,
        mandatory: true,
        applicant: applicant.name,
        status: applicant.residence ? CHECKLIST_STATE.COMPLETE : CHECKLIST_STATE.MISSING,
        reason: applicant.residence ? null : `Not captured — expected from ${AADHAAR}`
    });
    return rows;
};

/**
 * Document half of the checklist, read off the live `/case/<id>/checklist/`
 * response so it stays in step with the Doc Checklist tab and the PS Kit gate.
 * Leaf shape from the backend: { label, type, value, is_mandatory, ... }.
 */
const CHECKLIST_GROUP_LABELS = {
    "pre-sanction": "Pre-sanction",
    "post-sanction": "Post-sanction",
    Info: "Case info"
};

const leafStatus = (leaf, rejectedNames) => {
    const value = get(leaf, "value");
    const done = typeof value === "number" ? value > 0 : !!value;
    if (done) return CHECKLIST_STATE.COMPLETE;
    const label = String(get(leaf, "label") || "");
    if (rejectedNames.has(label.toLowerCase())) return CHECKLIST_STATE.WRONG;
    return CHECKLIST_STATE.MISSING;
};

/**
 * Checklist items the backend emits for every case but that only actually
 * apply to some constitutions. `/case/<id>/checklist/` has no constitution
 * gate of its own, so the rule is applied here, keyed by the backend's
 * document master id.
 *   14 = Company Bank Statement (Last 6 Months) - proprietorship only.
 */
export const CHECKLIST_CONSTITUTION_RULES = {
    14: [CONSTITUTION_TYPES.get("Proprietorship")]
};

const appliesToConstitution = (documentId, constitution) => {
    const allowed = CHECKLIST_CONSTITUTION_RULES[documentId];
    if (!allowed) return true;
    // Unknown constitution: keep the row rather than hide something real.
    if (constitution === null || constitution === undefined) return true;
    return allowed.indexOf(Number(constitution)) >= 0;
};

export const buildDocumentChecklist = (checklist, documents, constitution) => {
    if (!checklist) return [];
    // A document the reviewer has explicitly rejected reads as "Wrong" rather
    // than "Missing" — the file exists, it just failed validation.
    const rejectedNames = new Set(
        documents
            .filter(doc => Number(doc.status) === DOC_STATUS.REJECTED)
            .map(doc => String(doc.documentName || "").toLowerCase())
    );

    const rows = [];
    const pushLeaf = (leaf, groupLabel, subjectLabel) => {
        const label = get(leaf, "label");
        // Only `doc` leaves belong here; the `info` ones (phone, email,
        // address presence) are already covered by the form checklist, which
        // is derived from the applicant record itself.
        if (!label || get(leaf, "type") !== "doc") return;
        if (!appliesToConstitution(get(leaf, "document_id"), constitution)) return;
        const status = leafStatus(leaf, rejectedNames);
        rows.push({
            id: `${groupLabel}-${subjectLabel || ""}-${label}`,
            name: label,
            source: subjectLabel ? `${groupLabel} · ${subjectLabel}` : groupLabel,
            mandatory: !!get(leaf, "is_mandatory"),
            status,
            documentId: get(leaf, "document_id"),
            reason:
                status === CHECKLIST_STATE.COMPLETE
                    ? null
                    : status === CHECKLIST_STATE.WRONG
                        ? "On file but rejected on review — re-upload needed"
                        : "Not on file yet"
        });
    };

    const walkBucket = (bucket, groupLabel) => {
        Object.keys(bucket || {}).forEach(key => {
            const entry = bucket[key];
            if (!entry || typeof entry !== "object") return;
            if (get(entry, "label") && "value" in entry && typeof entry.value !== "object") {
                pushLeaf(entry, groupLabel);
                return;
            }
            const subject = get(entry, "label") || key;
            const value = get(entry, "value");
            if (Array.isArray(value)) {
                // Applicant KYC: one object of leaves per applicant.
                value.forEach((leafGroup, index) => {
                    const who =
                        getIn(leafGroup, ["name", "value"]) || `Applicant ${index + 1}`;
                    Object.keys(leafGroup || {}).forEach(leafKey => {
                        if (leafKey === "name") return;
                        pushLeaf(leafGroup[leafKey], subject, who);
                    });
                });
            } else if (value && typeof value === "object") {
                Object.keys(value).forEach(leafKey =>
                    pushLeaf(value[leafKey], subject)
                );
            } else if (get(entry, "label")) {
                pushLeaf(entry, groupLabel);
            }
        });
    };

    const plain = checklist && checklist.toJS ? checklist.toJS() : checklist;
    Object.keys(CHECKLIST_GROUP_LABELS).forEach(bucketKey => {
        if (plain[bucketKey]) {
            walkBucket(plain[bucketKey], CHECKLIST_GROUP_LABELS[bucketKey]);
        }
    });
    return rows.filter(row => row.name);
};

export const checklistTone = status => {
    if (status === CHECKLIST_STATE.COMPLETE) return "ok";
    if (status === CHECKLIST_STATE.WRONG) return "bad";
    return "warn";
};

export const checklistLabel = status => {
    if (status === CHECKLIST_STATE.COMPLETE) return "Complete";
    if (status === CHECKLIST_STATE.WRONG) return "Wrong";
    return "Missing";
};

/* ------------------------------------------------------ PS Kit re-trigger */

// Form field -> reason key under "applicant_details" in the backend reason
// master (apps/cases/services/pskit_retrigger.py). Unlisted fields leave the
// reason for the operator to pick.
const RETRIGGER_FIELD_REASONS = {
    first_name: "name_mismatch",
    last_name: "name_mismatch",
    name_pan: "name_mismatch",
    name_aadhar: "name_mismatch",
    name_gst: "name_mismatch",
    dob: "dob_mismatch",
    dob_pan: "dob_mismatch",
    dob_aadhar: "dob_mismatch",
    pan: "pan_incorrect",
    aadhar: "aadhaar_incorrect",
    father_name: "parent_spouse_name_incorrect",
    spouse_name: "parent_spouse_name_incorrect",
    mother_name: "parent_spouse_name_incorrect",
    gender: "gender_marital_incorrect",
    marital_status: "gender_marital_incorrect",
    phone: "contact_incorrect",
    phone_residence: "contact_incorrect",
    phone_office: "contact_incorrect",
    email: "contact_incorrect",
    nature_of_association: "designation_shareholding_incorrect",
    shareholding: "designation_shareholding_incorrect"
};

const RETRIGGER_ADDRESS_REASONS = {
    permanent_address: "permanent_address_incorrect",
    residence_address: "residence_address_incorrect"
};

/**
 * Can this checklist row be what a re-trigger reason points at? Keeps the
 * "Applies to" dropdown down to the rows that fit the picked reason, e.g.
 * "Name mismatch" lists only the name fields. A reason with no mapped fields
 * (like "Mandatory detail missing") keeps every row of its kind.
 */
export const rowFitsRetriggerReason = (row, category, reason) => {
    if (category === "documents") return !row.field;
    if (category !== "applicant_details" || !row.field || RETRIGGER_ADDRESS_REASONS[row.field]) {
        return false;
    }
    const mapped = Object.keys(RETRIGGER_FIELD_REASONS).filter(
        field => RETRIGGER_FIELD_REASONS[field] === reason
    );
    return mapped.length ? mapped.indexOf(row.field) >= 0 : true;
};

/**
 * Pre-filled re-trigger reason for a checklist row the operator flagged as
 * wrong. Form rows carry `field`; document rows do not.
 */
export const retriggerDraftFor = row => {
    const base = { rowId: row.id, item: row.name, applicant: row.applicant || "" };
    if (row.field && RETRIGGER_ADDRESS_REASONS[row.field]) {
        return { ...base, category: "address", reason: RETRIGGER_ADDRESS_REASONS[row.field] };
    }
    if (row.field) {
        const reason =
            RETRIGGER_FIELD_REASONS[row.field] ||
            (row.status === CHECKLIST_STATE.MISSING ? "field_missing" : null);
        return { ...base, category: "applicant_details", reason };
    }
    return {
        ...base,
        category: "documents",
        reason: row.status === CHECKLIST_STATE.WRONG ? "document_rejected" : "document_incorrect"
    };
};

/* ------------------------------------------------------ request documents */

// Same special cases as Document Review's request modal
// (templates/case-action-modals/document-request-modal.js): KYC documents
// need the applicant they are for, and "Others" needs a document name.
export const REQUEST_APPLICANT_DOCS = ["7", "6", "63", "63-12", "63-13"];
export const REQUEST_NAMED_DOCS = ["46"];
const REQUEST_HIDDEN_CATEGORY = "Progcap Others";

/**
 * Document master -> request picker groups. A type with options (Aadhaar
 * front / back) is offered per option, keyed "<id>-<option id>".
 */
export const buildRequestGroups = docMasterList => {
    const groups = {};
    toArray(docMasterList).forEach(item => {
        const id = get(item, "id");
        const name = get(item, "document_name");
        const category = getIn(item, ["document_category", "name"]) || "Other";
        if (!id || !name || category === REQUEST_HIDDEN_CATEGORY) return;
        const options = toArray(get(item, "document_document_type_option"));
        const entries = options.length
            ? options.map(option => ({
                value: `${id}-${get(option, "id")}`,
                label: get(option, "document_type_name") || name
            }))
            : [{ value: String(id), label: name }];
        groups[category] = (groups[category] || []).concat(entries);
    });
    return Object.keys(groups)
        .sort((a, b) => a.localeCompare(b))
        .map(category => ({
            category,
            items: groups[category].sort((a, b) => String(a.label).localeCompare(String(b.label)))
        }));
};

export const requestNeedsApplicant = value => REQUEST_APPLICANT_DOCS.indexOf(value) >= 0;
export const requestNeedsName = value => REQUEST_NAMED_DOCS.indexOf(value) >= 0;

/** First thing stopping the request from going out, or null. */
export const docRequestError = (selected, details) => {
    if (!selected.length) return "Pick at least one document to request.";
    for (const value of selected) {
        const detail = details[value] || {};
        if (requestNeedsApplicant(value) && !detail.associate) {
            return "Choose the applicant for each KYC document.";
        }
        if (requestNeedsName(value) && String(detail.remarks || "").trim().length < 5) {
            return "Name the document you need — at least 5 characters.";
        }
    }
    return null;
};

/**
 * The body Document Review sends: a plain id list when nothing needs extra
 * detail, otherwise { document: [...with applicant / name], documents: rest }
 * through the post-docs call.
 */
export const buildDocRequest = (selected, details) => {
    const toDoc = value => (value.indexOf("-") >= 0 ? value : Number(value));
    const special = selected.filter(v => requestNeedsApplicant(v) || requestNeedsName(v));
    if (!special.length) return { postDocs: false, docs: selected.map(toDoc) };
    return {
        postDocs: true,
        docs: {
            document: special.map(value => {
                const [id, option] = value.split("-");
                const detail = details[value] || {};
                const entry = { id: Number(id) };
                if (option) entry.document_option_id = Number(option);
                if (requestNeedsApplicant(value)) entry.associate = detail.associate;
                if (requestNeedsName(value)) entry.request_remarks = String(detail.remarks || "").trim();
                return entry;
            }),
            documents: selected.filter(v => special.indexOf(v) < 0).map(toDoc)
        }
    };
};

/* ---------------------------------------------------------- upload documents */

// The upload form's own list (forms/upload-company-doc-form.js).
export const UPLOAD_DOC_TYPES = [
    { value: "7", label: "Applicant PAN" },
    { value: "63-12", label: "Aadhaar Front" },
    { value: "63-13", label: "Aadhaar Back" },
    { value: "2", label: "Company GST" },
    { value: "6", label: "Applicant Photo" },
    { value: "56", label: "Post Sanction Kit" },
    { value: "42", label: "Others" },
    { value: "99", label: "Office CPV" },
    { value: "100", label: "Residence CPV" }
];
export const UPLOAD_APPLICANT_TYPES = ["7", "63-12", "63-13", "6", "100"];

/** File names already on the case that an upload would duplicate. */
export const duplicateFileNames = (files, existingNames) => {
    const existing = new Set(existingNames);
    return Array.from(new Set(files.map(f => f.name).filter(name => existing.has(name))));
};

/* ------------------------------------------------------- write payloads */

const idOf = value => {
    if (value === null || value === undefined || value === "") return null;
    if (typeof value === "object") return get(value, "id");
    return Number(value);
};

/**
 * Address half of the `associate-and-address-detail` payload.
 * `CreateOrUpdateResidenceAndPermanentAddressOfApplicantSerializer` wants the
 * masters as ids, the date as YYYY-MM-DD, and the type fixed per slot
 * (5 = residence, 6 = permanent).
 */
export const toAddressPayload = (address, { associateId, companyId, addressType }) => {
    if (!address) return null;
    return {
        id: get(address, "id") || null,
        company: companyId,
        associate: associateId || null,
        address_type: addressType,
        address_line_1: get(address, "address_line_1") || "",
        address_line_2: get(address, "address_line_2") || "",
        city: idOf(get(address, "city")),
        district: idOf(get(address, "district")),
        state: idOf(get(address, "state")),
        country: idOf(get(address, "country")) || 1,
        pincode: get(address, "pincode") || "",
        ownership_type: idOf(get(address, "ownership_type")),
        residing_since: get(address, "residing_since")
            ? isoDate(get(address, "residing_since"))
            : null,
        is_pv_done: !!get(address, "is_pv_done"),
        is_communication_address: !!get(address, "is_communication_address")
    };
};

// Fields the V2 endpoint must not receive back: read-only projections the GET
// adds, and relations it resolves itself.
const ASSOCIATE_READ_ONLY = [
    "residence_address",
    "permanent_address",
    "transaction_id",
    "vkyc_link",
    "key_source",
    "aadhar_pan_link_document",
    "cibil_report",
    "cibil_supported_report",
    "case_level_data",
    "create_date",
    "modified_date",
    "created_by",
    "modified_by"
];

/**
 * Build one entry of the `company/case/<id>/associate-and-address-detail/`
 * POST body — the same call `associate-new-form.js` makes.
 *
 * `raw` is the untouched GET row, so every field the operator did not edit is
 * echoed back unchanged; `draft` carries the edits.
 *
 * Note on the name: the GET serializes `first_name` as first + middle joined,
 * and the form edits that joined value under the label "First & Middle Name".
 * Echoing both back would re-append the middle name on every save (the value
 * grows each round trip), so the joined value is written to `first_name` and
 * `middle_name` is cleared.
 */
export const toAssociatePayload = (applicant, draft, { companyId }) => {
    const raw = applicant.raw;
    const plain = raw && raw.toJS ? raw.toJS() : Object.assign({}, raw);
    ASSOCIATE_READ_ONLY.forEach(key => delete plain[key]);

    const merged = Object.assign({}, plain, draft.fields || {});
    // Addresses are echoed back untouched. They are edited through
    // `address/create-update/` instead, which is the only path that can mint a
    // new city / district / state master row — the V2 serializer takes those as
    // plain integer ids and would reject a name-only master.
    const permanent = applicant.permanent;
    const residence = applicant.residence;

    return Object.assign({}, merged, {
        id: applicant.id,
        company: companyId,
        type: Number(draft.type !== undefined ? draft.type : applicant.type),
        middle_name: null,
        dob: merged.dob ? isoDate(merged.dob) : null,
        dob_pan: merged.dob_pan ? isoDate(merged.dob_pan) : null,
        dob_aadhar: merged.dob_aadhar ? isoDate(merged.dob_aadhar) : null,
        is_main_borrower: !!(draft.is_main_borrower !== undefined
            ? draft.is_main_borrower
            : applicant.isMainBorrower),
        is_key_contact: !!(draft.is_key_contact !== undefined
            ? draft.is_key_contact
            : applicant.isKeyContact),
        permanent_address: toAddressPayload(permanent, {
            associateId: applicant.id,
            companyId,
            addressType: 6
        }),
        residence_address: toAddressPayload(residence, {
            associateId: applicant.id,
            companyId,
            addressType: 5
        })
    });
};

const NAME_PATTERN = /^[a-zA-Z ]+$/;

// The V2 serializer requires both addresses in full, so an applicant cannot be
// saved until they exist. The message names the fix rather than the field.
const addressComplete = (address, label) => {
    const fix = `Add the ${label.toLowerCase()} address first (Edit on the ${label} Address block)`;
    if (!address) return `${label} address is missing. ${fix}.`;
    if (!get(address, "address_line_1")) return `${label} address has no address line 1. ${fix}.`;
    if (!/^[1-9][0-9]{5}$/.test(String(get(address, "pincode") || ""))) {
        return `${label} address needs a valid 6-digit pincode. ${fix}.`;
    }
    if (!get(address, "city")) return `${label} address needs a city. ${fix}.`;
    if (!get(address, "state")) return `${label} address needs a state. ${fix}.`;
    return null;
};

/**
 * Client-side checks that mirror what the backend and `associate-new-form`
 * enforce, so an operator gets a specific message instead of a 400.
 */
export const validateAssociatePayload = payload => {
    if (!payload.first_name || !String(payload.first_name).trim()) {
        return "First & middle name is required";
    }
    if (!NAME_PATTERN.test(String(payload.first_name))) {
        return "First & middle name may only contain letters and spaces";
    }
    if (!payload.last_name || !NAME_PATTERN.test(String(payload.last_name))) {
        return "Last name is required and may only contain letters and spaces";
    }
    if (payload.phone && !/^\d{10}$/.test(String(payload.phone))) {
        return "Phone number must be 10 digits";
    }
    // Both are mandatory on the case and both were saveable blank.
    if (!payload.gender) {
        return "Gender is required";
    }
    if (!payload.pan || !String(payload.pan).trim()) {
        return "PAN is required";
    }
    if (!/^[A-Za-z]{5}\d{4}[A-Za-z]$/.test(String(payload.pan).trim())) {
        return "PAN must look like ABCDE1234F";
    }
    if (payload.shareholding !== null && payload.shareholding !== undefined &&
        payload.shareholding !== "" && isNaN(Number(payload.shareholding))) {
        return "Shareholding must be a number";
    }
    return (
        addressComplete(payload.permanent_address, "Permanent") ||
        addressComplete(payload.residence_address, "Residence")
    );
};

/* Guarantor ids in APPLICANT_TYPES: 2 = Guarantor, 7 = D2R Guarantor. */
export const GUARANTOR_TYPES = [2, 7];

export const isGuarantorType = type => GUARANTOR_TYPES.includes(Number(type));

/**
 * Who an applicant can be on the case.
 *
 * A guarantor stands behind the borrowing, so they cannot also be the main
 * borrower, and a case cannot be made up of guarantors alone — somebody has to
 * be borrowing. Both are checked against the other applicants as they stand,
 * with the draft substituted for the one being edited.
 */
export const validateApplicantRoles = (applicants, editedId, draft) => {
    const type = draft.type;
    const guarantor = isGuarantorType(type);

    if (guarantor && draft.is_main_borrower) {
        return "A guarantor cannot also be the main borrower";
    }

    if (guarantor) {
        const others = applicants.filter(applicant => applicant.id !== editedId);
        if (others.length && others.every(applicant => isGuarantorType(applicant.type))) {
            return "At least one applicant has to be someone other than a guarantor";
        }
    }

    return null;
};

/**
 * Shareholding rule from `associate-new-form.js`: a proprietorship
 * (constitution 1) must total exactly 100%, anything else 51–100%. Checked
 * across all applicants, using the draft value for the one being saved.
 */
export const validateShareholding = (applicants, constitution, editedId, editedValue) => {
    const total = applicants.reduce((sum, applicant) => {
        const value =
            applicant.id === editedId ? editedValue : applicant.fields.shareholding;
        return sum + (Number(value) || 0);
    }, 0);
    if (Number(constitution) === 1) {
        return total === 100 ? null : `Shareholding across applicants must total 100% (currently ${total}%)`;
    }
    return total >= 51 && total <= 100
        ? null
        : `Shareholding across applicants must total 51–100% (currently ${total}%)`;
};

/* ------------------------------------------------------------- charges */

const slabLabel = slab => {
    const description = get(slab, "description");
    if (description) return String(description);
    const value = get(slab, "value");
    if (value === null || value === undefined) return null;
    const from = get(slab, "from_tenure");
    const to = get(slab, "to_tenure");
    return from || to ? `${value} (${from || 0}–${to || "∞"})` : String(value);
};

/**
 * Charges arrive in several shapes depending on which endpoint produced them:
 * a plain number, a numeric string, `{ configuration: [slab, …] }` (the case
 * defaults), a bare list of slabs (`bnpl/charge/schedule/`), or
 * `{ configuration: { charge_json: { charges } } }` (bounce charges).
 *
 * Everything here funnels to a string, because handing React one of those
 * objects as a child throws and takes the whole panel down with it.
 */
export const chargeText = value => {
    if (value === null || value === undefined || value === "") return null;
    if (typeof value === "number") return money(value);
    if (typeof value === "string") {
        const asNumber = Number(value);
        return value.trim() !== "" && !isNaN(asNumber) ? money(asNumber) : value;
    }

    const configuration = get(value, "configuration");

    // { configuration: { charge_json: { charges } } } — bounce charges.
    const nested = get(get(configuration, "charge_json"), "charges");
    if (nested !== null && nested !== undefined) return chargeText(nested);

    // { configuration: [slab, …] } or a bare list of slabs.
    const list = toArray(configuration || value);
    if (list.length) {
        const parts = list.map(slabLabel).filter(Boolean);
        if (parts.length) return parts.join(", ");
    }

    return slabLabel(value);
};

/**
 * A person as the API returns them — `{id, first_name, last_name, email,
 * mobile_number, full_name}` — reduced to something printable.
 */
export const personName = value => {
    if (!value) return null;
    if (typeof value === "string") return value;
    const full = get(value, "full_name");
    if (full) return String(full);
    const parts = [
        get(value, "first_name"),
        get(value, "middle_name"),
        get(value, "last_name")
    ].filter(Boolean);
    if (parts.length) return parts.join(" ");
    return get(value, "name") || get(value, "email") || null;
};
